import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout';

type ArticleCategory = 'all' | 'account' | 'learning' | 'billing' | 'technical';

const responseTargets = [
  { label: 'طلبات الحساب والدخول', value: 'خلال 24 ساعة عمل' },
  { label: 'مشكلات الدفع أو الاشتراك', value: 'خلال 1-2 يوم عمل' },
  { label: 'الأعطال التقنية المؤثرة', value: 'حسب الأولوية والتأثير' },
  { label: 'طلبات الخصوصية والبيانات', value: 'حسب نوع الطلب والمتطلبات النظامية' },
];

const contactChannels = [
  {
    title: 'نموذج الدعم',
    description: 'أفضل طريقة لإرسال تفاصيل المشكلة والحصول على رقم طلب.',
    action: 'إرسال طلب دعم',
    href: '/contact#contact-form',
    internal: true,
  },
  {
    title: 'البريد الإلكتروني',
    description: 'للاستفسارات العامة ومتابعة طلبات الدعم.',
    action: 'support@class-tech.net',
    href: 'mailto:support@class-tech.net',
    internal: false,
  },
  {
    title: 'طلبات الخصوصية',
    description: 'لطلبات الوصول إلى البيانات أو تصحيحها أو حذفها.',
    action: 'info@class-tech.net',
    href: 'mailto:info@class-tech.net',
    internal: false,
  },
];

const categories: Array<{ key: ArticleCategory; label: string }> = [
  { key: 'all', label: 'الكل' },
  { key: 'account', label: 'الحساب' },
  { key: 'learning', label: 'التشغيل التعليمي' },
  { key: 'billing', label: 'الدفع' },
  { key: 'technical', label: 'تقني' },
];

const articles = [
  {
    title: 'إنشاء حساب وتسجيل الدخول',
    category: 'account',
    summary:
      'استخدم /register لإنشاء حساب جديد أو /login للدخول. بعد الدخول، أكمل بيانات الحساب والإعدادات المطلوبة حسب دورك.',
  },
  {
    title: 'أدوار المستخدمين والصلاحيات',
    category: 'account',
    summary:
      'تدعم Class-Tech أدوار المعلم، الطالب، ولي الأمر، الموظف، والمدير. كل دور يرى الصفحات والبيانات المصرح بها فقط.',
  },
  {
    title: 'إدارة الطلاب والفصول',
    category: 'learning',
    summary:
      'يمكن للمعلم إدارة الطلاب والفصول والجداول والحضور والواجبات والتقارير من لوحة التحكم بعد تسجيل الدخول.',
  },
  {
    title: 'ربط الطلاب بأولياء الأمور',
    category: 'learning',
    summary:
      'تستخدم المنصة أكواد ربط مخصصة لمساعدة أولياء الأمور على متابعة الطلاب ضمن الصلاحيات المعتمدة.',
  },
  {
    title: 'الاشتراكات والمدفوعات',
    category: 'billing',
    summary:
      'تعرض صفحة الأسعار المعلومات العامة، وتتم إدارة الاشتراكات والمدفوعات من داخل الحساب عند توفر الصلاحية.',
  },
  {
    title: 'طلبات الاسترجاع',
    category: 'billing',
    summary:
      'يمكن طلب الاسترجاع خلال 7 أيام من تاريخ الدفع وفق شروط الاستخدام. أرسل رقم الحساب، البريد المستخدم، تاريخ الدفع، وسبب الطلب عبر نموذج الدعم أو support@class-tech.net.',
  },
  {
    title: 'مشكلات الاجتماعات أو الإشعارات',
    category: 'technical',
    summary:
      'عند وجود مشكلة تقنية، أرسل وصفًا واضحًا مع البريد المستخدم، نوع الجهاز، المتصفح، وخطوات إعادة المشكلة.',
  },
];

const SupportCenter: React.FC = () => {
  const [selectedCategory, setSelectedCategory] =
    useState<ArticleCategory>('all');

  const visibleArticles = articles.filter(
    article =>
      selectedCategory === 'all' || article.category === selectedCategory
  );

  return (
    <PublicLayout>
      <div className='bg-secondary-50 text-secondary-900 dark:bg-secondary-950 dark:text-white'>
        <section className='bg-white py-16 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <h1 className='text-3xl font-bold text-secondary-950 dark:text-white md:text-5xl'>
              مركز دعم Class-Tech
            </h1>
            <p className='mt-5 max-w-3xl text-lg leading-8 text-secondary-700 dark:text-secondary-200'>
              استخدم هذه الصفحة للوصول إلى قنوات الدعم الرسمية، معرفة أوقات
              الاستجابة المتوقعة، ومراجعة إرشادات التشغيل الأساسية قبل إرسال
              طلب مساعدة.
            </p>
            <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
              <Link
                to='/contact#contact-form'
                className='inline-flex min-h-11 items-center justify-center rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700'
              >
                إرسال طلب دعم
              </Link>
            </div>
          </div>
        </section>

        <section className='py-12'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
              {contactChannels.map(channel => (
                <article
                  key={channel.title}
                  className='rounded-lg border border-secondary-200 bg-white p-6 shadow-sm dark:border-secondary-800 dark:bg-secondary-900'
                >
                  <h2 className='text-xl font-bold text-secondary-950 dark:text-white'>
                    {channel.title}
                  </h2>
                  <p className='mt-3 leading-7 text-secondary-600 dark:text-secondary-300'>
                    {channel.description}
                  </p>
                  {channel.internal ? (
                    <Link
                      to={channel.href}
                      className='mt-5 inline-flex min-h-10 items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700'
                    >
                      {channel.action}
                    </Link>
                  ) : (
                    <a
                      href={channel.href}
                      className='mt-5 inline-flex min-h-10 items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700'
                    >
                      {channel.action}
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className='bg-white py-12 dark:bg-secondary-900'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
              <div>
                <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                  أوقات الاستجابة
                </h2>
                <p className='mt-3 leading-7 text-secondary-600 dark:text-secondary-300'>
                  تساعدنا التفاصيل الدقيقة على تشخيص الطلب بسرعة. أرفق البريد
                  المستخدم، الدور، وصف المشكلة، وأي خطوات لإعادة ظهورها.
                </p>
              </div>
              <div className='space-y-3 lg:col-span-2'>
                {responseTargets.map(target => (
                  <div
                    key={target.label}
                    className='flex flex-col gap-2 rounded-lg bg-secondary-50 p-4 dark:bg-secondary-800 sm:flex-row sm:items-center sm:justify-between'
                  >
                    <span className='font-semibold text-secondary-900 dark:text-white'>
                      {target.label}
                    </span>
                    <span className='text-sm text-secondary-600 dark:text-secondary-300'>
                      {target.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className='py-12'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
              <div>
                <h2 className='text-2xl font-bold text-secondary-950 dark:text-white'>
                  مقالات مساعدة أساسية
                </h2>
                <p className='mt-2 text-secondary-600 dark:text-secondary-300'>
                  إرشادات مختصرة للموضوعات الأكثر شيوعًا عند مراجعة أو تشغيل
                  المنصة.
                </p>
              </div>
              <div className='flex flex-wrap gap-2'>
                {categories.map(category => (
                  <button
                    key={category.key}
                    type='button'
                    onClick={() => setSelectedCategory(category.key)}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                      selectedCategory === category.key
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-secondary-700 hover:bg-secondary-100 dark:bg-secondary-900 dark:text-secondary-200 dark:hover:bg-secondary-800'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {visibleArticles.map(article => (
                <article
                  key={article.title}
                  className='rounded-lg border border-secondary-200 bg-white p-5 shadow-sm dark:border-secondary-800 dark:bg-secondary-900'
                >
                  <h3 className='font-bold text-secondary-950 dark:text-white'>
                    {article.title}
                  </h3>
                  <p className='mt-3 leading-7 text-secondary-700 dark:text-secondary-200'>
                    {article.summary}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default SupportCenter;
